import type { NomineeDetailResponse } from '@api/nominees-api';

export type NomineeTypeFilter = 'all' | 'clinic' | 'doctor';
export type NomineeVotedFilter = 'all' | 'voted' | 'not-voted';

export interface EditionNomineesExportOptions {
  readonly nomineeType: NomineeTypeFilter;
  readonly votedStatus: NomineeVotedFilter;
}

function escapeCsvValue(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function getNomineeFullName(nominee: NomineeDetailResponse): string {
  return [nominee.participant.name, nominee.participant.surname].filter(Boolean).join(' ');
}

export function filterNomineesForExport(
  nominees: readonly NomineeDetailResponse[],
  options: EditionNomineesExportOptions,
): NomineeDetailResponse[] {
  return nominees.filter((nominee) => {
    const matchesType =
      options.nomineeType === 'all' ||
      (options.nomineeType === 'clinic' && nominee.category.isClinic) ||
      (options.nomineeType === 'doctor' && !nominee.category.isClinic);

    const matchesVoted =
      options.votedStatus === 'all' ||
      (options.votedStatus === 'voted' && nominee.hasVotedInEdition) ||
      (options.votedStatus === 'not-voted' && !nominee.hasVotedInEdition);

    return matchesType && matchesVoted;
  });
}

export function buildEditionNomineesCsv(
  nominees: readonly NomineeDetailResponse[],
  options: EditionNomineesExportOptions,
): string {
  const rows = filterNomineesForExport(nominees, options).map((nominee) =>
    [
      nominee.participant.dpDoctorId.toString(),
      getNomineeFullName(nominee),
      nominee.participant.name,
      nominee.participant.surname ?? '',
      nominee.participant.city ?? '',
      nominee.category.name,
      nominee.category.isClinic ? 'Yes' : 'No',
      nominee.hasVotedInEdition ? 'Yes' : 'No',
      nominee.voteCount.toString(),
      nominee.ranking?.toString() ?? '',
      nominee.isWinner ? 'Yes' : 'No',
      nominee.participant.profileUrl ?? '',
    ]
      .map(escapeCsvValue)
      .join(','),
  );

  return [
    [
      'docplannerId',
      'nomineeName',
      'name',
      'surname',
      'city',
      'category',
      'isClinic',
      'hasVotedInEdition',
      'votesReceived',
      'ranking',
      'isWinner',
      'profileUrl',
    ].join(','),
    ...rows,
  ].join('\n');
}

export function getEditionNomineesCsvFilename(editionId: string, editionYear?: number): string {
  return editionYear != null
    ? `edition-nominees-${editionYear}.csv`
    : `edition-${editionId}-nominees.csv`;
}
